import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Microphone, PencilSimpleLine, ArrowRight, Sparkle, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VoiceToFormModal from "@/components/VoiceToFormModal";
import { usePropertyDetail } from "../context";

/**
 * DimmiTab — primo step del flusso annuncio · iter 310 r16
 *
 * Due strade per partire:
 *   • Voce → apre VoiceToFormModal INLINE (non più navigazione al
 *     Sopralluogo Live · resta nella tab Dimmi · esperienza coerente
 *     "sei sempre nell'annuncio")
 *   • Testo → vai alla tab "Testo" (passo 2)
 *
 * Quando l'utente conferma il transcript, chiamiamo PUT /properties/{id}
 * con i campi estratti dal backend e poi atterriamo sul passo 2 per
 * controllare/aggiustare.
 */
const ROUTES = [
  {
    key: "voce",
    Icon: Microphone,
    overline: "1A · Voce",
    title: "Detta a voce in 30 secondi",
    body: "Apri il registratore qui dentro e racconta l'annuncio come se parlassi a un collega. Pronto trascrive e tira fuori titolo, prezzo, mq, indirizzo, classe energetica.",
    bullets: [
      "Resti dentro l'annuncio",
      "Trascrizione automatica in italiano",
      "Puoi ripetere o correggere alla fine",
    ],
    accent: "#7C8B6E",
    cta: "Apri registratore",
  },
  {
    key: "scritto",
    Icon: PencilSimpleLine,
    overline: "1B · Scritto",
    title: "Scrivi a mano o componi dai dati",
    body: "Preferisci la tastiera o hai già i dati strutturati? Vai direttamente al passo 2 e scrivi/componi il testo. Più controllo, stessa rapidità.",
    bullets: [
      "Form classico + tono editoriale",
      "Pronto compone il testo dai dati",
      "Editor inline · niente magia nascosta",
    ],
    accent: "#C49A2A",
    cta: "Vai alla scrittura",
  },
];

export default function DimmiTab() {
  const { id, property, load, setTab } = usePropertyDetail();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const alreadyDescribed = !!property?.ai_description || !!property?.description;

  const go = (key) => {
    if (key === "voce") setVoiceOpen(true);
    else setTab("descrizione");
  };

  // Quando VoiceToFormModal estrae i campi, li salviamo nel property
  // corrente con PUT /properties/{id} preservando tutti i campi già
  // presenti (la PUT richiede l'oggetto completo).
  const onVoiceApply = async (fields, _transcript) => {
    setApplying(true);
    try {
      const merged = { ...property };
      let appliedCount = 0;
      Object.entries(fields || {}).forEach(([k, v]) => {
        if (v == null || v === "") return;
        // non sovrascrivere campi già compilati
        if (merged[k] && String(merged[k]).trim()) return;
        merged[k] = v;
        appliedCount += 1;
      });
      if (appliedCount === 0) {
        toast.info("Tutti i campi erano già compilati · vai a Testo per modificarli");
        setTab("descrizione");
        return;
      }
      await api.put(`/properties/${id}`, merged);
      await load();
      toast.success(`${appliedCount} ${appliedCount === 1 ? "campo aggiornato" : "campi aggiornati"} · vai a Testo per rivedere`);
      setTab("descrizione");
    } catch (e) {
      toast.error("Errore nel salvataggio · riprova");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mt-8" data-testid="dimmi-tab">
      <div className="max-w-3xl">
        <span className="overline">Step 1 · Racconta</span>
        <h2 className="font-editorial text-2xl sm:text-3xl mt-2 mb-2">
          Racconta l'annuncio · <span className="italic-accent text-[#7C8B6E]">come ti viene meglio</span>
        </h2>
        <p className="text-[13.5px] text-[#6B6A65] leading-snug mb-6">
          Niente form chilometrici. Scegli un modo per descriverci l'immobile: voce o tastiera.
          Pronto estrae i dati strutturati e nei passi successivi li controlli + aggiungi le foto.
        </p>

        {alreadyDescribed && property?.is_demo && (
          <div
            className="mb-6 p-4 bg-[#FAF1DC] border border-[#E8D9A8] rounded-lg flex items-start gap-3"
            data-testid="dimmi-demo-skip"
          >
            <div className="w-8 h-8 rounded-full bg-[#C49A2A] text-white flex items-center justify-center flex-shrink-0">
              <Check size={13} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1C1C1A]">
                Questo è l'annuncio demo — già pronto
              </div>
              <div className="text-[12px] text-[#3A3A36] mt-0.5">
                Salta al <button onClick={() => setTab("descrizione")} className="underline text-[#7C8B6E] hover:text-[#5d6952] font-medium">passo 2 · Testo</button> per vedere come Pronto compone il testo, oppure prova le funzioni voce/scrittura sul tuo prossimo annuncio.
              </div>
            </div>
          </div>
        )}

        {alreadyDescribed && !property?.is_demo && (
          <div
            className="mb-6 p-4 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-lg flex items-start gap-3"
            data-testid="dimmi-already-done"
          >
            <div className="w-8 h-8 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center flex-shrink-0">
              <Check size={13} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1C1C1A]">
                Hai già descritto l'annuncio
              </div>
              <div className="text-[12px] text-[#6B6A65] mt-0.5">
                Puoi passare direttamente al <button onClick={() => setTab("descrizione")} className="underline text-[#7C8B6E] hover:text-[#5d6952] font-medium">passo 2 · Testo</button> per rivedere titolo e dati.
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ROUTES.map(({ key, Icon, overline, title, body, bullets, accent, cta }, idx) => (
            <motion.button
              key={key}
              type="button"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 + idx * 0.15, ease: "easeOut" }}
              whileHover={{ y: -2 }}
              onClick={() => go(key)}
              disabled={applying}
              className="text-left bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] hover:shadow-md rounded-xl p-5 transition-all group disabled:opacity-60"
              data-testid={`dimmi-route-${key}`}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4 transition-transform group-hover:scale-105"
                style={{ background: `${accent}1A`, color: accent }}
              >
                <Icon size={22} weight="bold" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: accent }}>
                {overline}
              </div>
              <h3 className="font-editorial text-xl leading-tight text-[#1C1C1A] mb-2">
                {title}
              </h3>
              <p className="text-[12.5px] text-[#6B6A65] leading-snug mb-3">
                {body}
              </p>
              <ul className="space-y-1.5 mb-4">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-[12px] text-[#1C1C1A]">
                    <Check size={11} weight="bold" className="mt-1 flex-shrink-0" style={{ color: accent }} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#7C8B6E] group-hover:gap-1.5 transition-all">
                {cta} <ArrowRight size={12} weight="bold" />
              </div>
            </motion.button>
          ))}
        </div>

        <p className="mt-6 text-[11.5px] text-[#9A968B] italic">
          <Sparkle size={11} weight="fill" className="inline mr-1" style={{ color: "#C49A2A" }} />
          Suggerimento: la voce ti farà risparmiare ~5 minuti rispetto al form a mano · funziona meglio in posto silenzioso.
        </p>
      </div>

      {/* Modal voce INLINE · resta dentro l'annuncio */}
      <VoiceToFormModal
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onApply={onVoiceApply}
        existingFields={{
          title: property?.title || null,
          city: property?.city || null,
          price: property?.price || null,
          surface: property?.surface || null,
          rooms: property?.rooms || null,
          floor: property?.floor || null,
          energy_class: property?.energy_class || null,
          address: property?.address || null,
        }}
      />
    </div>
  );
}
